import React, { useState, useMemo } from 'react';
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  Stack,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  Divider,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ShieldIcon from '@mui/icons-material/Shield';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CallResponse } from 'retell-sdk/resources/call';
import { PiiPolicy } from 'assemblyai';

import {
  redactCallAudio,
  checkRedactedAudioStatus,
} from '../../api/hooks/calls/redactCallAudio';
import { toastError, toastSuccess } from '../../common/toastUtils';
import { getErrorMessage } from '../../common/errorUtils';
import { sleep } from '../../common/commonUtils';
import {
  PII_POLICY_CATEGORIES,
  PII_PRESETS,
  ALL_SELECTABLE_POLICIES,
  MINIMUM_REQUIRED_PII_POLICIES,
} from '../../constants/piiPolicies';

interface RecordingDownloadProps {
  call: CallResponse;
}

const POLLING_INTERVAL = 2500;

function resolveRecordingUrl(c: CallResponse): string | undefined {
  const record = c as unknown as Record<string, unknown>;
  if (typeof record.scrubbed_recording_url === 'string') {
    return record.scrubbed_recording_url;
  }
  return c.recording_url ?? undefined;
}

const RecordingDownloadButtonComponent = ({ call }: RecordingDownloadProps) => {
  const recordingUrl = resolveRecordingUrl(call);
  const [isRedacting, setIsRedacting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [progress, setProgress] = useState<number>(0);
  const [selectedPreset, setSelectedPreset] =
    useState<string>('Basic Protection');
  const [selectedPolicies, setSelectedPolicies] = useState<Set<PiiPolicy>>(
    new Set(
      PII_PRESETS.find((p) => p.name === 'Basic Protection')?.policies || []
    )
  );

  const categorySelectionCounts = useMemo(() => {
    const counts: Record<string, { selected: number; total: number }> = {};
    PII_POLICY_CATEGORIES.forEach((category) => {
      const selectablePolicies = category.policies.filter(
        (p) => !MINIMUM_REQUIRED_PII_POLICIES.includes(p.value)
      );
      const selected = selectablePolicies.filter((p) =>
        selectedPolicies.has(p.value)
      ).length;
      counts[category.name] = {
        selected,
        total: selectablePolicies.length,
      };
    });
    return counts;
  }, [selectedPolicies]);

  if (!recordingUrl) {
    return (
      <Tooltip title="No recording available!">
        <span>
          <IconButton disabled>
            <DownloadIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setShowDialog(true);
    setError(null);
    setStatusMessage('');
    setProgress(0);
  };

  const handleCloseDialog = () => {
    if (!isRedacting) {
      setShowDialog(false);
    }
  };

  const handlePresetChange = (presetName: string) => {
    setSelectedPreset(presetName);
    const preset = PII_PRESETS.find((p) => p.name === presetName);
    if (preset) {
      setSelectedPolicies(new Set(preset.policies));
    }
  };

  const handlePolicyToggle = (policy: PiiPolicy) => {
    if (MINIMUM_REQUIRED_PII_POLICIES.includes(policy)) {
      return;
    }

    const newPolicies = new Set(selectedPolicies);
    if (newPolicies.has(policy)) {
      newPolicies.delete(policy);
    } else {
      newPolicies.add(policy);
    }
    setSelectedPolicies(newPolicies);
    setSelectedPreset('Custom');
  };

  const handleCategoryToggle = (categoryName: string) => {
    const category = PII_POLICY_CATEGORIES.find((c) => c.name === categoryName);
    if (!category) return;

    const newPolicies = new Set(selectedPolicies);
    const selectablePolicies = category.policies
      .filter((p) => !MINIMUM_REQUIRED_PII_POLICIES.includes(p.value))
      .map((p) => p.value);

    const allSelected = selectablePolicies.every((p) => newPolicies.has(p));

    if (allSelected) {
      selectablePolicies.forEach((p) => newPolicies.delete(p));
    } else {
      selectablePolicies.forEach((p) => newPolicies.add(p));
    }

    setSelectedPolicies(newPolicies);
    setSelectedPreset('Custom');
  };

  const downloadFile = (url: string, isRedacted = true) => {
    const link = document.createElement('a');
    link.href = url;

    const originalFilename = url.split('/').pop() || 'recording.mp3';
    const fileExtension = originalFilename.includes('.')
      ? originalFilename.substring(originalFilename.lastIndexOf('.'))
      : '.mp3';

    const prefix = isRedacted ? 'redacted_call_' : 'call_';
    link.download = `${prefix}${call.call_id}${fileExtension}`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const pollTranscriptionStatus = async (
    transcriptId: string
  ): Promise<string | null> => {
    const maxAttempts = 60;
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const statusResponse = await checkRedactedAudioStatus({ transcriptId });

        setStatusMessage(`Processing: ${statusResponse.status}`);
        setProgress((attempts / maxAttempts) * 100);

        if (
          statusResponse.status === 'redacted_audio_ready' &&
          statusResponse.redacted_audio_url
        ) {
          setProgress(100);
          return statusResponse.redacted_audio_url;
        }

        attempts++;
      } catch (err) {
        console.error('Error checking status:', err);
        toastError(getErrorMessage(err));
      }
      await sleep(POLLING_INTERVAL);
    }

    return null;
  };

  const handleDownloadRedacted = async () => {
    if (!recordingUrl) return;

    setIsRedacting(true);
    setError(null);
    setStatusMessage('Initiating redaction...');
    setProgress(5);

    try {
      const policiesToSend = Array.from(selectedPolicies);

      const transcriptResult = await redactCallAudio({
        mediaUrl: recordingUrl,
        piiPolicies: policiesToSend,
      });

      const transcriptId = transcriptResult.id;

      if (!transcriptId) {
        toastError('No transcript ID received');
        return;
      }

      setStatusMessage('Processing audio transcription...');
      setProgress(10);

      const redactedAudioUrl = await pollTranscriptionStatus(transcriptId);

      if (redactedAudioUrl) {
        downloadFile(redactedAudioUrl, true);
        handleCloseDialog();
        toastSuccess('Successfully downloaded call with redacted audio!');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Redaction failed';
      setError(errorMessage);
      console.error('Redaction error:', err);
    } finally {
      setIsRedacting(false);
      setStatusMessage('');
      setProgress(0);
    }
  };

  return (
    <>
      <Tooltip title="Download Recording (PII Customizable)" arrow>
        <IconButton
          onClick={handleClick}
          color="primary"
          sx={{
            '&:hover': {
              backgroundColor: 'primary.light',
              color: 'white',
            },
          }}
        >
          <DownloadIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        open={showDialog}
        onClose={handleCloseDialog}
        aria-labelledby="download-dialog-title"
        aria-describedby="download-dialog-description"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="download-dialog-title">
          <Stack direction="row" alignItems="center" spacing={1}>
            <DownloadIcon color="primary" />
            <Typography variant="h6">Download Call Recording</Typography>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            id="download-dialog-description"
            component="div"
            sx={{ mb: 2 }}
          >
            <Typography variant="body1" sx={{ mb: 2 }}>
              Select what type of personal information to redact from the
              recording:
            </Typography>

            {/* Preset Selection using Chips */}
            <Typography
              variant="subtitle2"
              sx={{ mb: 1.5, fontWeight: 'bold' }}
            >
              Redaction Level
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {[
                ...PII_PRESETS.filter((p) => p.name !== 'Custom'),
                {
                  name: 'Custom',
                  policies: [] as PiiPolicy[],
                  description: 'Select specific categories',
                },
              ].map((preset) => {
                const isCustom = preset.name === 'Custom';
                const isSelected = selectedPreset === preset.name;
                const chipLabel =
                  preset.name === 'None'
                    ? 'None'
                    : preset.name === 'Basic Protection'
                      ? 'Basic'
                      : preset.name === 'Healthcare'
                        ? 'Healthcare'
                        : preset.name === 'Financial'
                          ? 'Financial'
                          : preset.name === 'Everything'
                            ? 'Everything'
                            : preset.name === 'Custom'
                              ? 'Custom'
                              : preset.name;

                const chipHelper =
                  isCustom && selectedPolicies.size > 0
                    ? `${selectedPolicies.size} selected`
                    : preset.policies.length === 0
                      ? 'Card/banking only'
                      : preset.policies.length ===
                          ALL_SELECTABLE_POLICIES.length
                        ? 'All PII'
                        : `${preset.policies.length} items`;

                return (
                  <Box key={preset.name} sx={{ width: 'calc(33.333% - 8px)' }}>
                    <Chip
                      label={
                        <Box
                          sx={{
                            textAlign: 'center',
                            py: 0.5,
                            minHeight: '48px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: isSelected ? 'bold' : 'normal' }}
                          >
                            {chipLabel}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.65rem',
                              opacity: 0.8,
                              display: 'block',
                            }}
                          >
                            {chipHelper}
                          </Typography>
                        </Box>
                      }
                      onClick={() => handlePresetChange(preset.name)}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      sx={{
                        width: '100%',
                        height: '64px',
                        '& .MuiChip-label': {
                          display: 'block',
                          whiteSpace: 'normal',
                          px: 1,
                          py: 0.5,
                          width: '100%',
                        },
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: isSelected
                            ? undefined
                            : 'action.hover',
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>

            {/* Show preset description */}
            {selectedPreset !== 'Custom' && (
              <Alert
                severity={selectedPreset === 'None' ? 'warning' : 'info'}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  {
                    PII_PRESETS.find((p) => p.name === selectedPreset)
                      ?.description
                  }
                </Typography>
              </Alert>
            )}

            {/* Custom Selection - Category checkboxes */}
            {selectedPreset === 'Custom' && (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}
                >
                  Customize by Category
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ mb: 2, display: 'block', color: 'text.secondary' }}
                >
                  Select entire categories or expand to choose specific items
                </Typography>
                <Box
                  sx={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {PII_POLICY_CATEGORIES.map((category) => (
                    <Accordion
                      key={category.name}
                      defaultExpanded={false}
                      sx={{
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        backgroundColor: 'transparent',
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`${category.name}-content`}
                        id={`${category.name}-header`}
                        sx={{ px: 0 }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          sx={{ width: '100%' }}
                        >
                          <Checkbox
                            checked={
                              categorySelectionCounts[category.name]
                                .selected ===
                              categorySelectionCounts[category.name].total
                            }
                            indeterminate={
                              categorySelectionCounts[category.name].selected >
                                0 &&
                              categorySelectionCounts[category.name].selected <
                                categorySelectionCounts[category.name].total
                            }
                            onChange={() => handleCategoryToggle(category.name)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {category.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {category.description}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={`${categorySelectionCounts[category.name].selected}/${categorySelectionCounts[category.name].total}`}
                            color={
                              categorySelectionCounts[category.name].selected >
                              0
                                ? 'primary'
                                : 'default'
                            }
                            variant={
                              categorySelectionCounts[category.name].selected >
                              0
                                ? 'filled'
                                : 'outlined'
                            }
                          />
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pl: 5 }}>
                        <FormGroup>
                          {category.policies
                            .filter(
                              (policy) =>
                                !MINIMUM_REQUIRED_PII_POLICIES.includes(
                                  policy.value
                                )
                            )
                            .map((policy) => (
                              <FormControlLabel
                                key={policy.value}
                                control={
                                  <Checkbox
                                    checked={selectedPolicies.has(policy.value)}
                                    onChange={() =>
                                      handlePolicyToggle(policy.value)
                                    }
                                    size="small"
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="body2">
                                      {policy.label}
                                    </Typography>
                                    {policy.description && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {policy.description}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            ))}
                        </FormGroup>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            <Alert severity="info" sx={{ mb: 0 }}>
              <Typography variant="body2">
                {selectedPolicies.size === 0
                  ? 'Card and banking information (credit cards, CVV, expiration dates, account numbers, and banking info) will always be redacted for security.'
                  : `${selectedPolicies.size} additional PII type${
                      selectedPolicies.size === 1 ? '' : 's'
                    } selected. Card and banking information is always redacted for security. Processing may take a few moments.`}
              </Typography>
            </Alert>
          </DialogContentText>

          {statusMessage && (
            <Box sx={{ mb: 2 }}>
              <Alert
                severity="info"
                icon={<CircularProgress size={20} />}
                sx={{ mb: 1 }}
              >
                {statusMessage}
              </Alert>
              {isRedacting && (
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ borderRadius: 1 }}
                />
              )}
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDialog}
            disabled={isRedacting}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDownloadRedacted}
            variant="contained"
            disabled={isRedacting}
            startIcon={
              isRedacting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <ShieldIcon />
              )
            }
          >
            {isRedacting ? 'Processing...' : 'Download Recording'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RecordingDownloadButtonComponent;
